import { Big } from "big.js";
import { CostType } from "../../shared/CaseConfiguration";
import { Party } from "../../shared/CaseModel";
import {
  Assessment,
  RecoverableCosts,
  RecoveryOrder,
} from "../../shared/judgment";
import { amountOf } from "./CloseStageDialog";

export const COST_TYPES: CostType[] = [
  "lawFirmFee",
  "barristerFee",
  "courtFee",
  "expertFee",
  "otherDisbursement",
];

/** A winner's recovery as the dialog holds it while it is entered. */
export type RecoveryDraft = {
  mode: RecoveryOrder["mode"];
  // in % per cost type, and the one share
  percent: { [type in CostType]?: number };
  oneShare: number;
  of: Assessment["of"];
  // what is entered of each kind of assessment: they mean different things
  assessed: { [kind in Assessment["of"]]?: number };
  // an assessment being edited
  draft?: string;
};

const costsOf = ({ byType }: RecoverableCosts) =>
  COST_TYPES.reduce((sum, type) => sum.add(byType[type] ?? 0), new Big(0));

/** What the estimates of the case come to, the recovery expected. */
export const expectedOf = (
  recoverable: RecoverableCosts,
  estimates: { [type in CostType]?: number },
) =>
  COST_TYPES.reduce(
    (sum, type) =>
      sum.add(
        new Big(recoverable.byType[type] ?? 0)
          .mul(estimates[type] ?? 0)
          .div(100),
      ),
    new Big(0),
  );

/** A party's recovery estimates in %, by cost type. */
export const estimatesOf = (party: Party | undefined) =>
  Object.fromEntries(
    (party?.recoveries ?? []).map(({ type, percentage }) => [
      type,
      new Big(percentage).mul(100).toNumber(),
    ]),
  ) as { [type in CostType]?: number };

/** The draft of a recovery: as ordered, or at the estimates of the case. */
export function draftOf(
  recoverable: RecoverableCosts,
  party: Party | undefined,
  order: RecoveryOrder | undefined,
): RecoveryDraft {
  const estimates = estimatesOf(party);
  const percent = order
    ? (Object.fromEntries(
        Object.entries(order.shares).map(([type, share]) => [
          type,
          new Big(share).mul(100).toNumber(),
        ]),
      ) as RecoveryDraft["percent"])
    : estimates;
  const costs = costsOf(recoverable);
  return {
    mode: order?.mode ?? "byType",
    percent,
    oneShare: costs.gt(0)
      ? expectedOf(recoverable, percent).div(costs).mul(100).round().toNumber()
      : 0,
    of: order?.assessment?.of ?? "costs",
    assessed: order?.assessment
      ? { [order.assessment.of]: order.assessment.amount }
      : {},
  };
}

/** The share of a cost type in %, under the mode of the draft. */
export const shareOf = (draft: RecoveryDraft, type: CostType): number =>
  draft.mode === "oneShare" ? draft.oneShare : (draft.percent[type] ?? 0);

/** What the shares come to: the subtotal of the recovery. */
export const subtotalOf = (
  { byType }: RecoverableCosts,
  draft: RecoveryDraft,
): Big =>
  COST_TYPES.reduce(
    (sum, type) =>
      sum.add(new Big(byType[type] ?? 0).mul(shareOf(draft, type)).div(100)),
    new Big(0),
  );

export const weightedShareOf = (
  recoverable: RecoverableCosts,
  draft: RecoveryDraft,
): Big => {
  const costs = costsOf(recoverable);
  return costs.gt(0)
    ? subtotalOf(recoverable, draft).div(costs).mul(100)
    : new Big(0);
};

/** What the winner recovers: the assessment where there is one, or the shares. */
export function recoveryOf(
  recoverable: RecoverableCosts,
  draft: RecoveryDraft,
  assessed: number | undefined = draft.assessed[draft.of],
): Big {
  if (assessed === undefined) return subtotalOf(recoverable, draft);
  return draft.of === "recovery"
    ? new Big(assessed)
    : new Big(assessed).mul(weightedShareOf(recoverable, draft)).div(100);
}

/** The order as the judgment stores it. */
export function orderOf(draft: RecoveryDraft): RecoveryOrder {
  const assessed = draft.assessed[draft.of];
  return {
    mode: draft.mode,
    shares: Object.fromEntries(
      COST_TYPES.map((type) => [
        type,
        new Big(shareOf(draft, type)).div(100).toNumber(),
      ]),
    ),
    ...(assessed !== undefined && {
      assessment: { of: draft.of, amount: assessed },
    }),
  };
}

export const isValidAssessment = (text: string) =>
  text.trim() === "" || amountOf(text) !== undefined;
